import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Remixer {
  user_id: string;
  user_initials: string;
}

export interface FavoritePrompt {
  favorite_id: string;
  score_id: string;
  overall_score: number;
  detected_type: string | null;
  best_prompt: string | null;
  original_content: string | null;
  submission_type: string | null;
  dimension_scores_json: Record<string, any> | null;
  evaluation_markdown: string | null;
  chatcraft_subscores_json: Record<string, number> | null;
  points_total: number | null;
  points_base: number | null;
  points_rubric: number | null;
  points_bonus: number | null;
  favorited_at: string;
  created_at: string;
  is_own_prompt: boolean;
  owner_name: string;
  owner_id: string;
  owner_initials: string;
  favorite_count: number;
  remix_count: number;
  recent_remixers: Remixer[];
  tags: string[];
}

export function useFavorites(userId: string | undefined) {
  return useQuery({
    queryKey: ['favorites', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase.rpc('get_user_favorites', { 
        _user_id: userId 
      });
      
      if (error) {
        console.error('Error fetching favorites:', error);
        return [];
      }
      
      // Transform the data to ensure proper typing
      return (data || []).map((item: any) => ({
        ...item,
        recent_remixers: Array.isArray(item.recent_remixers) ? item.recent_remixers : [],
        favorite_count: item.favorite_count || 0,
        remix_count: item.remix_count || 0,
        tags: Array.isArray(item.tags) ? item.tags : [],
      })) as FavoritePrompt[];
    },
    enabled: !!userId,
    staleTime: 1000 * 60, // 1 minute
  });
}

export function useToggleFavorite() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      userId, 
      scoreId, 
      isFavorited 
    }: { 
      userId: string; 
      scoreId: string; 
      isFavorited: boolean;
    }) => {
      if (isFavorited) {
        // Remove favorite
        const { error } = await supabase
          .from('favorite_prompts')
          .delete()
          .eq('user_id', userId)
          .eq('score_id', scoreId);
        
        if (error) throw error;
        return { action: 'removed' };
      } else {
        // Add favorite
        const { error } = await supabase
          .from('favorite_prompts')
          .insert({ user_id: userId, score_id: scoreId });
        
        if (error) throw error;
        return { action: 'added' };
      }
    },
    onSuccess: (result, { userId, scoreId }) => {
      // Invalidate both caches:
      // 1. The favorites list (for FavoritePrompts section)
      queryClient.invalidateQueries({ queryKey: ['favorites', userId] });
      // 2. The individual favorited status (for star button state)
      queryClient.invalidateQueries({ queryKey: ['favorited', userId, scoreId] });
      
      toast.success(result.action === 'added' 
        ? 'Added to favorites' 
        : 'Removed from favorites'
      );
    },
    onError: (error) => {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite');
    }
  });
}

export function useCheckFavorited(userId: string | undefined, scoreId: string) {
  return useQuery({
    queryKey: ['favorited', userId, scoreId],
    queryFn: async () => {
      if (!userId) return false;
      
      const { data, error } = await supabase.rpc('check_is_favorited', {
        _user_id: userId,
        _score_id: scoreId
      });
      
      if (error) {
        console.error('Error checking favorite status:', error);
        return false;
      }
      
      return !!data;
    },
    enabled: !!userId && !!scoreId,
    staleTime: 1000 * 30, // 30 seconds
  });
}
